import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, FileText, Upload, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Projetos › Inventário Executivo — a tela de leitura (o link permanente)
// ---------------------------------------------------------------------------
// Mostra a versão vigente do inventário do Sr. Marco. O conteúdo é o arquivo
// HTML que ele publica, renderizado como veio — o portal não reescreve nada
// (salvo a ponte de navegação explicada mais abaixo, que só ACRESCENTA).
//
// ⚠️ POR QUE UM IFRAME COM SANDBOX (e não injetar o HTML na página)
// O arquivo traz <script> inline e é gerado por IA fora do portal, várias vezes
// por dia, sem ninguém auditar. Se rodasse dentro da página do portal, esse
// script leria o authToken do localStorage e poderia agir como o usuário logado.
//
// O sandbox SEM `allow-same-origin` joga o documento num "origin opaco": os
// scripts dele continuam funcionando, mas ficam cegos para localStorage,
// cookies e DOM do portal.
//
// ⚠️ NUNCA acrescentar `allow-same-origin` a este iframe, e NUNCA oferecer um
//    "abrir em nova aba" apontando para o conteúdo — aberto no topo, sem
//    sandbox, o script do arquivo volta a rodar no origin do portal.
//
// ⚠️ POR QUE srcdoc E UMA PONTE DE NAVEGAÇÃO (corrigido em 2026-08-03)
// A primeira versão usava uma `blob:` URL, apostando que ela preservaria as
// âncoras internas do arquivo (o Radar navega por #ex-0002; os botões "Abrir
// painel"/"Voltar ao painel" apontam para #top). Estava ERRADO: um documento em
// origin opaco NÃO PODE navegar para uma blob: URL — nem para trocar só o
// fragmento —, então o navegador bloqueia o clique e nada acontece.
//
// Agora o HTML entra por `srcdoc` e o portal injeta um script curto que
// intercepta os cliques em links de âncora e rola por `scrollIntoView`. Assim a
// navegação não depende de o navegador permitir a mudança de URL — que é
// justamente o que o isolamento impede.

const COR = '#7c3aed'; // violeta — identidade do módulo Projetos

/**
 * Ponte de navegação injetada no HTML publicado.
 *
 * O arquivo navega por âncoras (<a href="#ex-0002">, <a href="#top">). Dentro do
 * sandbox essa navegação é bloqueada, então aqui ela vira rolagem por JS — mesmo
 * efeito visual, sem depender de mudar a URL do documento.
 *
 * Só ACRESCENTA comportamento: nada do conteúdo é alterado ou removido.
 */
// Montado por concatenação para que a sequência "</script>" nunca apareça
// inteira neste arquivo — se algum dia o bundle for embutido dentro de um
// <script> no HTML, um literal fecharia a tag no meio do código.
const FIM_SCRIPT = '<' + '/script>';

const PONTE_NAVEGACAO = `
<script>
(function () {
  document.addEventListener('click', function (ev) {
    var alvoClique = ev.target;
    if (!alvoClique || !alvoClique.closest) return;
    var link = alvoClique.closest('a[href^="#"]');
    if (!link) return;

    ev.preventDefault();
    var id = link.getAttribute('href').slice(1);

    // "Voltar ao painel" / "Abrir painel" apontam para o topo da página.
    if (!id) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }

    var destino = document.getElementById(id);
    if (destino) destino.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, true);
})();
${FIM_SCRIPT}
`;

/** Injeta a ponte no fim do documento publicado (antes de </body>). */
function comPonteDeNavegacao(html) {
    if (/<\/body>/i.test(html)) return html.replace(/<\/body>/i, PONTE_NAVEGACAO + '</body>');
    return html + PONTE_NAVEGACAO; // arquivo sem </body> — acrescenta no fim
}

const ProjetosInventario = ({ onIrParaEnvio, podeEnviar }) => {
    const [meta, setMeta] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [documento, setDocumento] = useState('');
    const [telaCheia, setTelaCheia] = useState(false);

    // Tela estreita (celular): os botões da faixa ficam só com o ícone. Precisa
    // acompanhar o giro do aparelho — de retrato para paisagem o rótulo cabe.
    const [estreito, setEstreito] = useState(() => window.innerWidth <= 640);
    useEffect(() => {
        const aoRedimensionar = () => setEstreito(window.innerWidth <= 640);
        window.addEventListener('resize', aoRedimensionar);
        return () => window.removeEventListener('resize', aoRedimensionar);
    }, []);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro('');
        try {
            const token = localStorage.getItem('authToken');
            const r = await fetch('/api/projetos/inventario', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error(`Não foi possível carregar o inventário (${r.status}).`);
            const dados = await r.json();

            if (dados.vazio) { setMeta({ vazio: true }); return; }

            setDocumento(comPonteDeNavegacao(dados.html));
            setMeta(dados);
        } catch (e) {
            setErro(e.message);
            setMeta(null);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    // ESC sai da tela cheia — é o reflexo de quem está lendo em tela inteira.
    useEffect(() => {
        if (!telaCheia) return;
        const aoTeclar = (e) => { if (e.key === 'Escape') setTelaCheia(false); };
        window.addEventListener('keydown', aoTeclar);
        // Trava a rolagem da página de trás, senão ela rola por baixo do overlay.
        const overflowAnterior = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', aoTeclar);
            document.body.style.overflow = overflowAnterior;
        };
    }, [telaCheia]);

    const formatarData = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    // ----- Estados que não são o conteúdo -----

    if (carregando) {
        return (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '14px', fontWeight: 600 }}>Carregando o inventário…</p>
                <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
            </div>
        );
    }

    if (erro) {
        return (
            <div style={{
                padding: '24px', borderRadius: '12px', backgroundColor: '#fef2f2',
                border: '1px solid #fecaca', color: '#991b1b', display: 'flex', gap: '12px',
            }}>
                <AlertCircle size={22} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                    <strong style={{ display: 'block', marginBottom: '4px' }}>Não foi possível abrir o inventário</strong>
                    <span style={{ fontSize: '14px' }}>{erro}</span>
                    <div>
                        <button onClick={carregar} style={{
                            marginTop: '14px', padding: '8px 16px', borderRadius: '8px', border: 'none',
                            backgroundColor: '#991b1b', color: '#fff', fontWeight: 600, cursor: 'pointer',
                        }}>Tentar de novo</button>
                    </div>
                </div>
            </div>
        );
    }

    // Ainda ninguém publicou. É estado normal (módulo novo), não erro.
    if (meta?.vazio) {
        return (
            <div style={{
                padding: '60px 32px', textAlign: 'center', borderRadius: '16px',
                border: '2px dashed #ddd6fe', backgroundColor: '#faf5ff', margin: '24px',
            }}>
                <FileText size={44} color={COR} />
                <h3 style={{ margin: '18px 0 8px', color: '#0f172a', fontSize: '20px', fontWeight: 800 }}>
                    Nenhuma versão publicada ainda
                </h3>
                <p style={{ color: '#64748b', margin: '0 auto', maxWidth: '440px', lineHeight: 1.6 }}>
                    Assim que a primeira versão do inventário for enviada, ela aparece aqui —
                    e este endereço passa a ser o link permanente.
                </p>
                {podeEnviar && (
                    <button onClick={onIrParaEnvio} style={{
                        marginTop: '22px', padding: '11px 22px', borderRadius: '10px', border: 'none',
                        backgroundColor: COR, color: '#fff', fontWeight: 700, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                    }}>
                        <Upload size={18} /> Enviar a primeira versão
                    </button>
                )}
            </div>
        );
    }

    // ----- O conteúdo -----

    // O quadro do documento. Em tela cheia ocupa a viewport inteira; encaixado,
    // ocupa toda a altura que sobra abaixo do cabeçalho e das abas.
    const quadro = (
        <iframe
            title="Inventário Executivo"
            srcDoc={documento}
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            allow="clipboard-write"
            style={{
                display: 'block', width: '100%', border: 'none', backgroundColor: '#f4f6f8',
                height: telaCheia ? 'calc(100vh - 52px)' : 'calc(100vh - 232px)',
                minHeight: telaCheia ? 0 : '520px',
            }}
        />
    );

    // Faixa de procedência: de qual versão é o que está na tela, e quando entrou.
    //
    // ⚠️ UMA LINHA SÓ, altura fixa de 52px (pedido do Sr. Marco, 2026-08-04). A
    //    versão anterior usava `flexWrap: 'wrap'` e, no celular, os três blocos
    //    quebravam em TRÊS linhas — a faixa passava de 140px e empurrava o
    //    inventário para baixo da dobra. Era esse o "desproporcional": não a
    //    altura declarada (já era 52), e sim a quebra.
    //
    // ⚠️ Esta faixa é CONTROLE DE EDIÇÃO, não informação de leitura. Por isso a
    //    hierarquia: versão e data ficam visíveis, a AUTORIA saiu para o tooltip
    //    (segue a um toque de distância, e a aba "Publicar versão" a lista por
    //    extenso no histórico, que é onde ela é de fato consultada).
    //
    // ⚠️ NADA aqui pode encolher os botões: `flexShrink: 0` neles e na versão;
    //    quem cede espaço é a data (trunca com reticências). Sem isso, numa tela
    //    estreita o "Tela cheia" viraria uma lasca inclicável.
    const rotuloAutoria = `Publicado por ${meta.enviadoPor || '—'} em ${formatarData(meta.dataEnvio)}`;

    const barra = (
        <div style={{
            display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '12px',
            padding: estreito ? '0 10px' : '0 14px', backgroundColor: '#faf5ff',
            borderBottom: '1px solid #ede9fe',
            height: '52px', boxSizing: 'border-box', overflow: 'hidden',
        }}>
            <div
                title={rotuloAutoria}
                style={{
                    display: 'flex', alignItems: 'center', gap: '7px',
                    color: COR, fontWeight: 800, fontSize: '13.5px', flexShrink: 0,
                }}
            >
                <FileText size={16} style={{ flexShrink: 0 }} />
                <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {meta.versao || 'sem carimbo'}
                </span>
            </div>

            {/* Único bloco que cede espaço — trunca antes de empurrar os botões. */}
            <span
                title={rotuloAutoria}
                style={{
                    fontSize: '12.5px', color: '#64748b', fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
                }}
            >
                {formatarData(meta.dataEnvio)}
            </span>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '7px', flexShrink: 0 }}>
                <button onClick={carregar} title="Buscar a versão mais recente" style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: estreito ? '7px 9px' : '6px 12px', borderRadius: '8px',
                    border: '1px solid #ddd6fe', backgroundColor: '#fff', color: COR,
                    fontWeight: 600, cursor: 'pointer', fontSize: '12.5px', whiteSpace: 'nowrap',
                }}>
                    <RefreshCw size={14} />
                    {/* No estreito ficam só os ícones: rótulo + rótulo não cabem
                        sem espremer os botões a ponto de errar o toque. */}
                    {!estreito && 'Atualizar'}
                </button>
                <button
                    onClick={() => setTelaCheia((v) => !v)}
                    title={telaCheia ? 'Sair da tela cheia (Esc)' : 'Ver em tela cheia'}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: estreito ? '7px 9px' : '6px 12px', borderRadius: '8px',
                        border: 'none', backgroundColor: COR, color: '#fff',
                        fontWeight: 600, cursor: 'pointer', fontSize: '12.5px', whiteSpace: 'nowrap',
                    }}
                >
                    {telaCheia ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                    {!estreito && (telaCheia ? 'Sair' : 'Tela cheia')}
                </button>
            </div>
        </div>
    );

    // Tela cheia: overlay por cima de tudo (o cabeçalho do módulo tem z-index 30).
    if (telaCheia) {
        return (
            <div style={{
                position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: '#fff',
                display: 'flex', flexDirection: 'column',
            }}>
                {barra}
                {quadro}
            </div>
        );
    }

    return (
        <div style={{
            border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden',
            backgroundColor: '#fff',
        }}>
            {barra}
            {quadro}
        </div>
    );
};

export default ProjetosInventario;
